import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { createPlan, planImpact, updatePlan } from '@/lib/api/admin';
import { describeError } from '@/lib/api/client';
import type { AdminPlan, FeatureKey, LimitKey, PlanImpactReport } from '@/lib/api/types';
import { formatNumber, plural } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Notice } from '@/components/ui/feedback';
import { Field, Input, inputClass } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { BLANK_FORM, FEATURE_HINT, FEATURE_KEYS, FEATURE_LABEL, LIMIT_KEYS, LIMIT_LABEL, suggestCode, toForm, validate, yearlyDiscount, type PlanForm } from './plan-form';

export function ImpactReport({ report }: { report: PlanImpactReport }) {
  const clean = report.affectedOrganizations === 0 && report.featuresRemoved.length === 0;
  if (clean) return <Notice tone="ok" icon={<CheckCircle2 size={16} />} title="Никто из подписчиков не выходит за новые лимиты" />;
  return (
    <Notice tone="warn" icon={<AlertTriangle size={16} />} title={`Организаций за лимитом: ${report.affectedOrganizations} из ${report.subscriberCount}`}>
      {report.featuresRemoved.length > 0 && <p>Отключаемые возможности: {report.featuresRemoved.map((k) => FEATURE_LABEL[k as FeatureKey] ?? k).join(', ')}</p>}
      {report.breaches.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {report.breaches.map((b) => (
            <li key={`${b.organizationId}-${b.resource}`}>
              <span className="font-semibold">{b.organizationName}</span> — {LIMIT_LABEL[b.resource as LimitKey] ?? b.resource}: {formatNumber(b.current)} при лимите {formatNumber(b.proposedLimit)}
            </li>
          ))}
        </ul>
      )}
    </Notice>
  );
}

type SaveOutcome = { kind: 'saved' } | { kind: 'blocked'; report: PlanImpactReport };

/**
 * Create, edit, or duplicate a plan.
 *
 * `limits`/`features` are replaced WHOLE, never merged key-by-key, so every
 * field is always in the form and always sent together. A price change
 * reaches only NEW subscriptions (an active one renews at its locked amount),
 * so raising a price needs no warning — tightening a LIMIT does. Hence every
 * save of an existing plan runs the impact dry run first, and a change that
 * would leave an organization over a limit or without a feature stops for a
 * second, explicit confirmation.
 */
export function PlanEditor({ plan, template, onClose }: {
  /** The plan being edited; null creates. */
  plan: AdminPlan | null;
  /** For a duplicate: the plan whose values seed a NEW plan. */
  template?: AdminPlan | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isCreate = plan === null;
  const [form, setForm] = useState<PlanForm>(() => plan ? toForm(plan) : template ? { ...toForm(template), code: suggestCode(`${template.name} copy`), name: `${template.name} (копия)` } : BLANK_FORM);
  const [codeTouched, setCodeTouched] = useState(false);
  const [report, setReport] = useState<PlanImpactReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const patch = (next: Partial<PlanForm>) => { setError(null); setForm((prev) => ({ ...prev, ...next })); };
  // A report describes the limits it was computed for; editing them makes it a lie.
  const setLimit = (key: LimitKey, value: string) => { setReport(null); patch({ limits: { ...form.limits, [key]: value } }); };
  const setFeature = (key: FeatureKey, value: boolean) => { setReport(null); patch({ features: { ...form.features, [key]: value } }); };
  const setName = (name: string) => patch(isCreate && !codeTouched ? { name, code: suggestCode(name) } : { name });

  const preview = useMutation({
    mutationFn: async () => {
      if (!plan) throw new Error('Сначала сохраните тариф');
      const v = validate(form, false);
      if (!v.ok) throw new Error(v.error);
      return planImpact(plan.id, { limits: v.value.limits, features: v.value.features });
    },
    onSuccess: setReport,
    onError: (err) => setError(describeError(err)),
  });

  const save = useMutation({
    mutationFn: async ({ force }: { force: boolean }): Promise<SaveOutcome> => {
      const v = validate(form, isCreate);
      if (!v.ok) throw new Error(v.error);
      const { code, ...editable } = v.value;
      if (!plan) { await createPlan({ code, ...editable }); return { kind: 'saved' }; }
      if (!force) {
        const r = await planImpact(plan.id, { limits: editable.limits, features: editable.features });
        if (r.affectedOrganizations > 0 || r.featuresRemoved.length > 0) return { kind: 'blocked', report: r };
      }
      await updatePlan(plan.id, editable);
      return { kind: 'saved' };
    },
    onSuccess: (outcome) => {
      if (outcome.kind === 'blocked') { setReport(outcome.report); return; }
      toast('success', isCreate ? 'Тариф создан' : 'Тариф сохранён', form.name);
      qc.invalidateQueries({ queryKey: ['admin', 'plans'] });
      onClose();
    },
    onError: (err) => setError(describeError(err)),
  });

  const blocked = report !== null && (report.affectedOrganizations > 0 || report.featuresRemoved.length > 0);
  const discount = yearlyDiscount(Number(form.priceMonthly), Number(form.priceYearly));

  // The report and any error land at the foot of a scrolling dialog, after a click at its foot.
  const foot = useRef<HTMLDivElement>(null);
  useEffect(() => { if (report || error) foot.current?.scrollIntoView({ block: 'end', behavior: 'smooth' }); }, [report, error]);

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={isCreate ? (template ? `Копия тарифа «${template.name}»` : 'Новый тариф') : `Тариф «${plan.name}»`}
      description={isCreate ? 'Новый тариф появится на странице тарифов сразу, если он активен.' : `${plural(plan.subscriberCount, ['подписка', 'подписки', 'подписок'])} на этом тарифе`}
      footer={(
        <>
          {!isCreate && <Button variant="ghost" loading={preview.isPending} onClick={() => preview.mutate()} className="mr-auto">Проверить влияние</Button>}
          <Button variant="outline" onClick={onClose}>Отмена</Button>
          {blocked
            ? <Button variant="danger" loading={save.isPending} onClick={() => save.mutate({ force: true })}>Сохранить всё равно</Button>
            : <Button loading={save.isPending} onClick={() => save.mutate({ force: false })}>{isCreate ? 'Создать' : 'Сохранить'}</Button>}
        </>
      )}
    >
      <div className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-[1fr_200px]">
          <Input label="Название" value={form.name} onChange={(e) => setName(e.target.value)} maxLength={120} autoFocus />
          {isCreate ? (
            <Input label="Код" value={form.code} onChange={(e) => { setCodeTouched(true); patch({ code: e.target.value.toLowerCase() }); }} placeholder="starter" autoCapitalize="none" autoCorrect="off" spellCheck={false} className="font-mono" hint="Изменить потом нельзя" />
          ) : (
            <Field label="Код" hint="Не редактируется: на него ссылаются подписки и счета"><div className={`${inputClass} flex items-center bg-surface-2 font-mono`}>{plan.code}</div></Field>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-[1fr_1fr_100px]">
          <Input label="Цена в месяц" inputMode="decimal" value={form.priceMonthly} onChange={(e) => patch({ priceMonthly: e.target.value })} />
          <Input label="Цена в год" inputMode="decimal" value={form.priceYearly} onChange={(e) => patch({ priceYearly: e.target.value })}
            hint={discount !== null ? (discount > 0 ? `скидка ${discount} % против 12 месяцев` : discount < 0 ? `дороже 12 месяцев на ${-discount} %` : 'ровно 12 месяцев') : undefined} />
          <Input label="Валюта" value={form.currency} onChange={(e) => patch({ currency: e.target.value.toUpperCase() })} maxLength={3} className="font-mono uppercase" />
        </div>
        <p className="-mt-3 text-xs text-faint">Новая цена действует только для новых подписок — активные продлеваются по зафиксированной сумме.</p>

        <section>
          <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted">Лимиты</h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {LIMIT_KEYS.map((k) => <Input key={k} label={LIMIT_LABEL[k]} inputMode="numeric" placeholder="∞" value={form.limits[k]} onChange={(e) => setLimit(k, e.target.value)} />)}
          </div>
          <p className="mt-1.5 text-xs text-faint">Пустое поле = без ограничений</p>
        </section>

        <section>
          <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted">Возможности</h3>
          <ul className="divide-y divide-line">
            {FEATURE_KEYS.map((k) => (
              <li key={k} className="flex items-center justify-between gap-4 py-2.5">
                <div><p className="text-sm text-ink">{FEATURE_LABEL[k]}</p><p className="text-xs text-faint">{FEATURE_HINT[k]}</p></div>
                <Switch checked={form.features[k]} onChange={(v) => setFeature(k, v)} label={FEATURE_LABEL[k]} />
              </li>
            ))}
          </ul>
        </section>

        <div className="flex items-center justify-between gap-4 border-t border-line pt-4">
          <div><p className="text-sm text-ink">Активен</p><p className="text-xs text-faint">Неактивный тариф исчезает со страницы тарифов, подписчики остаются на нём</p></div>
          <Switch checked={form.isActive} onChange={(v) => patch({ isActive: v })} label="Активен" />
        </div>

        {(report || error) && (
          <div ref={foot} className="space-y-3">
            {report && <ImpactReport report={report} />}
            {blocked && <p className="text-xs text-muted">Сохранение остановлено: изменение затронет подписчиков выше. Чтобы применить его всё равно, нажмите «Сохранить всё равно».</p>}
            {error && <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-xs text-danger">{error}</p>}
          </div>
        )}
      </div>
    </Dialog>
  );
}
